/**
 * Plain-English explanation of each Fair Price anchor.
 *
 * Kept out of the primitive and the tooltip alike so the wording is defined
 * once and both the chart and any future settings copy read from the same
 * place. The anchor rules live in `utt-engine/src/strategy/fair_price.rs`;
 * these summarise them for someone reading a label mid-session, not someone
 * reading the source.
 */
import type { FairPriceAnchorKind } from '../../store/fairPriceStore';

export interface AnchorHelp {
  /** Full name, since the on-chart label is abbreviated for width. */
  title: string;
  /** What the level IS. */
  what: string;
  /** Why it is on the chart — what it is used for. */
  why: string;
}

export const ANCHOR_HELP: Record<FairPriceAnchorKind, AnchorHelp> = {
  rth929: {
    title: 'RTH opening body (9:29)',
    what: "The body of the 9:29 ET candle — the last bar before the regular session opens.",
    why: 'The session’s starting fair price. Distance and direction for RTH signals are measured from here.',
  },
  open6pm: {
    title: 'Globex open (6:00 PM ET)',
    what: 'Opening price of the first candle at or after the 6:00 PM ET futures session open.',
    why: 'Fair price for the evening session, before Asia is properly underway.',
  },
  open8pm: {
    title: 'Asia open (8:00 PM ET)',
    what: 'Opening price of the first candle at or after 8:00 PM ET.',
    why: 'Fair price once Asian hours take over from the Globex open.',
  },
  open2pm: {
    title: 'PM session open (2:00 PM ET)',
    what: 'Opening price of the first candle at or after 2:00 PM ET.',
    why: 'Re-anchors the afternoon, so late-session signals are not measured against a stale morning level.',
  },
  prenews: {
    title: 'Pre-news body',
    what: 'Body of the last candle before a red-folder event — US, high importance — from the news calendar.',
    why: 'The last price agreed on before the release. Where the market considered fair with the news still unknown.',
  },
  migrated: {
    title: 'Fair price moved here',
    what: 'A volume spike pushed price away from the previous anchor, price failed to come back, then it consolidated at this new level.',
    why: 'The old anchor is treated as dead for the rest of the session. Every distance and direction is measured from this level instead.',
  },
};

/**
 * Extra detail for the migrated anchor only.
 *
 * It is the one anchor produced by a rule rather than by a clock, so "why is
 * this here?" has a real answer worth showing. The thresholds are settings, so
 * this describes the shape of the test rather than quoting numbers that would
 * go stale the moment they were changed.
 */
export const MIGRATION_CONDITIONS: readonly string[] = [
  'Volume spike that closed well away from the old anchor, moving away from it',
  'Price then failed to revert to the old anchor within the look-ahead window',
  'Price consolidated into a tight range, forming this box',
];
